import React from 'react';
import { Calendar, Palmtree, Briefcase, Home } from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from './ui/sheet';

export default function LeaveBalanceSheet({ employee, onClose }) {
  return (
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-md bg-[#0a0a0f] border-l border-white/10 overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle className="text-white font-heading text-2xl font-bold" data-testid="leave-balance-title">{employee.employee_name}</SheetTitle>
          <p className="font-mono text-cyan-400 text-sm">{employee.employee_id}</p>
        </SheetHeader>
        <div className="space-y-6">
          <Section title="Yearly Leaves" subtitle="Resets on joining anniversary">
            <LeaveCard icon={<Palmtree className="text-rose-400" size={20} />} label="Sick Leave" balance={employee.sick_leave} total={8} color="rose" />
            <LeaveCard icon={<Calendar className="text-cyan-400" size={20} />} label="Casual Leave" balance={employee.casual_leave} total={8} color="cyan" />
            <LeaveCard icon={<Briefcase className="text-indigo-400" size={20} />} label="Earned Leave" balance={employee.earned_leave} total={8} color="indigo" />
          </Section>

          <Section title="WFH Balance">
            <div className="flex items-center gap-4">
              <div className="p-2 rounded-lg bg-white/5"><Home className="text-indigo-400" size={20} /></div>
              <div className="flex-1">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-slate-300">WFH Balance</span>
                  <span className={`font-mono font-bold text-lg ${(employee.wfh_allowed || 1) - (employee.wfh_used || 0) < 0 ? 'text-rose-400' : 'text-white'}`}>{(employee.wfh_allowed || 1) - (employee.wfh_used || 0)} / {employee.wfh_allowed || 1}</span>
                </div>
                <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${(employee.wfh_allowed || 1) - (employee.wfh_used || 0) < 0 ? 'bg-rose-400' : 'bg-indigo-400'}`} style={{ width: `${Math.min(Math.max(((employee.wfh_allowed || 1) - (employee.wfh_used || 0)) / (employee.wfh_allowed || 1) * 100, 5), 100)}%` }} />
                </div>
                <p className="text-[10px] text-slate-500 mt-1">Team WFH days are not counted. Used: {employee.wfh_used || 0}</p>
              </div>
            </div>
          </Section>

          <div className="glass-card p-4">
            <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3">Reset Information</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Last Yearly Reset</span>
                <span className="text-slate-300">{formatDate(employee.last_yearly_reset)}</span>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="glass-card p-4">
      <div className="mb-4">
        <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function LeaveCard({ icon, label, balance, total, color }) {
  const pct = (balance / total) * 100;
  const isLow = balance < 2;
  const isNeg = balance < 0;
  const bgColors = { rose: 'bg-rose-400', cyan: 'bg-cyan-400', indigo: 'bg-indigo-400' };
  return (
    <div className="flex items-center gap-4">
      <div className="p-2 rounded-lg bg-white/5">{icon}</div>
      <div className="flex-1">
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm text-slate-300">{label}</span>
          <span className={`font-mono font-bold ${isNeg ? 'text-rose-400' : isLow ? 'text-amber-400' : 'text-white'}`}>{balance} / {total}</span>
        </div>
        <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${isNeg ? 'bg-rose-400' : isLow ? 'bg-amber-400' : bgColors[color]}`} style={{ width: `${Math.min(Math.max(pct, 5), 100)}%` }} />
        </div>
      </div>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}
